import { Stack, Modal, Pagination, createStyles, Alert, Skeleton } from '@mantine/core';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from '@emotion/react';
import useTranslation from 'next-translate/useTranslation';

import repeat from '~/common/utils/repeat';
import dt from '~/testing';
import { GroupEntryViewsProvider } from '~/domains/group/components/GroupEntryViewsProvider/GroupEntryViewsProvider';

import useMyContestEntries from './useMyContestEntries';
import MyEntriesContent from './MyEntriesContent';

const useStyles = createStyles((theme) => ({
  pagination: {
    background: theme.white,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ 20,
    width: '100%',
    position: 'fixed',
    bottom: 0,
    left: 0,
    zIndex: 9,
  },
}));

const ITEMS_COUNT = 10;

function getPagesCount(total: number) {
  return Math.ceil(total / ITEMS_COUNT);
}

type MyEntriesModalProps = {
  contestId: string;
  scheduleId?: string;
  userId?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialTotal: number;
  tiersCount: number;
  myEntriesStatus: string;
  leagueAlias?: string;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

function MyEntriesModal({
  contestId,
  scheduleId,
  userId,
  isOpen,
  onClose,
  tiersCount,
  leagueAlias,
  initialTotal,
  myEntriesStatus,
  openEntryNameEditModal,
}: MyEntriesModalProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  const { t } = useTranslation('contest');

  const [page, setPage] = useState(1);

  const { entries, slateStatus, entriesForCurrentUser, isLoading, isError, slateId } =
    useMyContestEntries({
      contestId,
      scheduleId,
      userId,
      pageSize: ITEMS_COUNT,
      page,
      enabled: isOpen,
    });

  return (
    <Modal
      onClose={onClose}
      opened={isOpen}
      title={t('detail.entries.title', { status: myEntriesStatus })}
      size="lg"
      radius="md"
      fullScreen
      data-test-id={dt.contestDetail.section.myEntries.modal}
    >
      <Stack spacing={0} /* eslint-disable-line local-rules/use-theme-tokens */ mb={20}>
        {isLoading && !isError && (
          <Stack spacing="lg">
            {repeat(ITEMS_COUNT, (index) => (
              <Skeleton
                h={isTablet ? 122 : 81}
                w="100%"
                key={index}
                data-test-id={dt.contestDetail.section.myEntries.skeleton}
              />
            ))}
          </Stack>
        )}
        {!isLoading && isError && <Alert color="red">{t('detail.entries.error')}</Alert>}
        {!isLoading && !isError &&
          (userId ? (
            <GroupEntryViewsProvider
              entryIds={entries?.map(({ id }) => id) ?? []}
              userId={userId}
            >
              <MyEntriesContent
                contestId={contestId}
                entries={entries}
                slateStatus={slateStatus}
                tiersCount={tiersCount}
                leagueAlias={leagueAlias}
                slateId={slateId}
                openEntryNameEditModal={openEntryNameEditModal}
              />
            </GroupEntryViewsProvider>
          ) : (
            <MyEntriesContent
              contestId={contestId}
              entries={entries}
              slateStatus={slateStatus}
              tiersCount={tiersCount}
              leagueAlias={leagueAlias}
              slateId={slateId}
              openEntryNameEditModal={openEntryNameEditModal}
            />
          ))}
        <Pagination
          total={getPagesCount(entriesForCurrentUser?.total ?? initialTotal)}
          page={page}
          onChange={setPage}
          withEdges
          size="sm"
          siblings={4}
          disabled={isLoading}
          className={classes.pagination}
        />
      </Stack>
    </Modal>
  );
}

export default MyEntriesModal;
